import { styles } from "@material-ui/pickers/views/Calendar/Calendar";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "../../components/DataTable";
import PageContent from "../../components/PageContent";
import Search from "../../components/Search";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { apiRequest } from "../../utils/api"
import useDocumentTitle from "../../utils/useDocumentTitle";
import { dayJS } from "../../utils/datetime";


async function getOverallCoachingReport({
    page,
    limit,
    sessionId
}) {
    const { data } = await apiRequest.get("/api/getOverallCoachingReport", {
        params: {
            page,
            limit,
            session_id: sessionId,
            tz: dayJS().format("Z") // viewer's UTC offset, e.g. "+08:00"; backend applies CONVERT_TZ
        }
    })

    const flatData = data.data.flatMap(session => {
        // Session level fields — shared across every subject row of this session
        const sessionFields = {
            session_id: session.session_id,
            session_created_date: session.session_created_date,
            is_closed: session.is_closed,
            coaching_date: session.coaching_date,
            method: session.method,
            review_date: session.review_date,
            review_time: session.review_time,
            actual_coaching_date: session.actual_coaching_date,
            archive_session_timestamp: session.archive_session_timestamp,
            campaign_name: session.campaign?.campaign_name,
            sub_functional_unit_name: session.campaign?.sub_functional_unit_name,
            coach: session.coach?.coach,
            coach_employee_id: session.coach?.userStaff?.employee_id,
            country: session.coach?.region?.country,
            coachee: session.coachee?.coachee,
            coachee_employee_id: session.coachee?.userStaff?.employee_id,
            type_of_session: session.sessionType?.type_of_session,
            goals: session.growForm?.goals,
            options: session.growForm?.options,
            way_forward: session.growForm?.way_forward,
            acknowledged_time: session.growForm?.acknowledged_time,
            question1: session.growForm?.question1,
            question2: session.growForm?.question2,
            question3: session.growForm?.question3,
            question4: session.growForm?.question4,
            question5: session.growForm?.question5,
            question6: session.growForm?.question6,
            question7: session.growForm?.question7,
            review_date_current: session.review?.review_date_current,
            review_time_current: session.review?.review_time_current,
            summary: session.review?.summary,
            coachee_behaviour_rating: session.review?.coachee_behaviour_rating,
            coachee_satisfaction: session.review?.coachee_satisfaction,
            plan_timing: session.durations?.plan_timing,
            do_timing: session.durations?.do_timing,
            check_timing: session.durations?.check_timing,
            act_timing: session.durations?.act_timing,
            total_duration: session.durations?.total_duration,
            notes: session.notes,
        };

        return (session.sessionSubjects ?? []).map(subject => {
            const detail = session.subjectDetails?.find(d => d.subject_id === subject.subject_id);
            return {
                ...sessionFields,
                // Subject level fields
                subject_id: subject.subject_id,
                link: subject.link,
                project_target_kpi: subject.project_target_kpi,
                current_score: subject.current_score,
                main_subject_name: subject.srm?.main_subject_name,
                sub_subject_name: subject.srm?.sub_subject_name,
                framework: subject.srm?.framework,
                condition_to_pass: subject.srm?.condition_to_pass,
                final_review_pass_fail: subject.srm?.final_review_pass_fail,
                project_target_kpi_score: subject.srm?.project_target_kpi_score,
                skill_to_improve: detail?.skill_to_improve,
                target: detail?.target,
                final_review_score: detail?.final_review_score,
                checkpoints: subject.checkpoints,
            };
        });
    });


    return {
        total: data.total,
        page: data.page,
        limit: data.limit,
        data: flatData
    };
}

const columns = [
    {
        id: "session_id",
        label: "Session ID",
        sortable: false,
    },
    {
        id: "session_created_date",
        label: "Session (Created Date)",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "country",
        label: "Country",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "campaign_name",
        label: "Functional Unit",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "sub_functional_unit_name",
        label: "Sub Functional Unit",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "coach",
        label: "Coach",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "coach_employee_id",
        label: "Coach EID",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "coachee",
        label: "Coachee",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "coachee_employee_id",
        label: "Coachee EID",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "coaching_date",
        label: "Coaching Date",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "method",
        label: "Method",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "type_of_session",
        label: "Session Type",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "subject_id",
        label: "Subject ID",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "main_subject_name",
        label: "Subject Category",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "sub_subject_name",
        label: "Subject Name",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "link",
        label: "Insert Link",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "review_date",
        label: "Session Review Date",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "review_time",
        label: "Session Review Time",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "goals",
        label: "GROW (Goals)",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "project_target_kpi",
        label: "GROW (Reality) Project Target KPI",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "current_score",
        label: "GROW (Reality) Current Metric",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "target",
        label: "GROW (Reality) Coachee's Target Metric",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "skill_to_improve",
        label: "Skill to Improve",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "options",
        label: "GROW (Options)",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "way_forward",
        label: "GROW (Way Forward)",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "question1",
        label: "Conversational 1",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "question2",
        label: "Conversational 2",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "question3",
        label: "Conversational 3",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "question4",
        label: "Conversational 4",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "question5",
        label: "Conversational 5",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "question6",
        label: "Conversational 6",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "question7",
        label: "Conversational 7",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "plan_timing",
        label: "(PLAN) Duration",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "acknowledged_time",
        label: "(DO) Acknowledgement Coaching Date & Time",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "actual_coaching_date",
        label: "(DO) Coaching Date & Time",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "do_timing",
        label: "(DO) Duration",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "checkpoints",
        label: "Checkpoints",
        render: (checkpoints) => (
            checkpoints?.length > 0
                ? checkpoints.map((c, i) => (
                    <div key={i}>{c.checkpoint_date}: {c.checkpoint_score}</div>
                ))
                : "-"
        )
    },
    {
        id: "check_timing",
        label: "(CHECK) Duration",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "review_date_current",
        label: "(ACT) Review Date",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "review_time_current",
        label: "(ACT) Review Time",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "summary",
        label: "(ACT) Review Notes",
        sortable: false,
        minWidth: 520,
        render: (value) => value || "-",
    },
    {
        id: "act_timing",
        label: "(ACT) Duration",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "archive_session_timestamp",
        label: "(ACT) Archive Date",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "total_duration",
        label: "Total Session Duration",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "final_review_score",
        label: "Final Session Score",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "final_review_pass_fail",
        label: "Session Success",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "project_target_kpi_score",
        label: "Project Target KPI Success",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "coachee_satisfaction",
        label: "Coachee's Behaviour Rating",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "coachee_behaviour_rating",
        label: "Happiness Rating",
        sortable: false,
        render: (value) => value || "-",
    },
    {
        id: "notes",
        label: "Notes",
        minWidth: 720,
        render: (notes) => (
            notes
                ? <>
                    {notes.key_points?.length > 0 && (
                        <div>
                            <strong>Key Points:</strong>
                            {notes.key_points.map((n, i) => (
                                <div key={i}>{n.note} <span>— <b><i>{n.note_by}</i></b></span></div>
                            ))}
                        </div>
                    )}
                    {notes.casual_notes?.length > 0 && (
                        <div>
                            <strong>Casual Notes:</strong>
                            {notes.casual_notes.map((n, i) => (
                                <div key={i}>{n.note} <span>— <b><i>{n.note_by}</i></b></span></div>
                            ))}
                        </div>
                    )}
                    {notes.compliments?.length > 0 && (
                        <div>
                            <strong>Compliments:</strong>
                            {notes.compliments.map((n, i) => (
                                <div key={i}>
                                    {n.note} <span>— <b><i>{n.note_by}</i></b></span>
                                </div>
                            ))}
                        </div>
                    )}
                    {!notes.key_points?.length && !notes.casual_notes?.length && !notes.compliments?.length && "-"}
                </>
                : "-"
        )
    },
]

export function OverallCoachingReport(props) {
    useDocumentTitle(props.title)
    const { t } = useTranslation();

    const [sessionId, setSessionId] = useState("")

    const filters = useMemo(
        () => ({
            sessionId
        }),
        [sessionId]
    )

    return (
        <DashboardLayout>
            <div className={styles.pageWrapper}>
                <PageContent>
                    <Section>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <SectionTitle>{t("leftNavigation.overallCoachingReport")}</SectionTitle>
                        </div>
                        <div className={styles.filterWrapper}>
                            <div className={styles.search}>
                                <Search
                                    handleSearch={val => {
                                        setSessionId(val);
                                    }}
                                />
                            </div>
                        </div>
                        <DataTable
                            columns={columns}
                            fetchData={getOverallCoachingReport}
                            filters={filters}
                        />
                    </Section>
                </PageContent>
            </div>
        </DashboardLayout>
    )
}